import {useKpiNodeIds} from '@hconnect/common/hooks'
import {filterSxStyles} from '@hconnect/common/styles'
import {KpiOption} from '@hconnect/common/types'
import {Autocomplete, TextField, Paper, MenuItem, PaperProps} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_FILTERS_WIDGET_NAMESPACE} from '../consts'

type Props = {
  'data-test-id'?: string
  plantId: string
  label?: React.ReactNode
  onChange?: (term?: KpiOption) => void
  placeholder?: string
  value?: KpiOption
  removeFilter: (keyName: string) => void
}

const paper = (props: PaperProps) => <Paper elevation={8} style={{minWidth: 350}} {...props} />

export const StoppedMainEquipmentField: React.FC<Props> = ({
  plantId,
  onChange = () => undefined,
  value: selectedEquipment,
  removeFilter,
  ...props
}: Props) => {
  const {t} = useTranslation(SHIFT_EVENT_FILTERS_WIDGET_NAMESPACE)
  const [open, setOpen] = React.useState(false)
  const [searchTerm, setSelectedSearchTerm] = React.useState<string | undefined>(undefined)

  const departmentOptions = useKpiNodeIds(plantId)
  const optionsToRender = searchTerm
    ? departmentOptions.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : departmentOptions

  return (
    <Autocomplete
      data-test-id={props['data-test-id']}
      value={selectedEquipment ?? null}
      onChange={(event, next, reason) => {
        if (reason === 'clear') {
          if (selectedEquipment || searchTerm) {
            // first rest the filter
            onChange(undefined)
            setSelectedSearchTerm(undefined)
          } else {
            // then remove it
            removeFilter('stoppedMainEquipment')
          }
        } else if (next && typeof next !== 'string' && next.id !== selectedEquipment?.id) {
          // new selected equipment
          onChange(next)
        }
      }}
      onInputChange={(event, newValue, reason) => {
        if (reason === 'input' && newValue !== searchTerm) {
          setSelectedSearchTerm(newValue)
        }
      }}
      open={open && departmentOptions.length > 0}
      onOpen={() => {
        setOpen(true)
      }}
      onClose={() => {
        setOpen(false)
      }}
      options={optionsToRender}
      sx={{
        marginRight: {xs: 2, md: 0}
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('shiftEvent.label.stoppedMainEquipment')}
          placeholder={t('pleaseStartTyping')}
          fullWidth={false}
          sx={filterSxStyles.textInputField}
          variant="filled"
        />
      )}
      PaperComponent={paper}
      forcePopupIcon={false}
      renderOption={(renderProps, option) => (
        <MenuItem {...renderProps} key={`${option.id}-${option.label}`}>
          {option.label}
        </MenuItem>
      )}
      getOptionLabel={(equipment) => (equipment ? equipment.label : '-')}
      // per default the auto-complete component will use the getOptionLabel,
      // compare it with the given input text and will filter out all that do not match.
      // that is a problem when having sapNumbers were we remove the leading zeros
      // 00000008321003 will be rendered as 8321003 and therefor not match if the input is 0083
      // and will not be presented as an option
      // to avoid this behavior, we override the filterOptions function to force it to compare on object level
      filterOptions={(options) => options}
      isOptionEqualToValue={(a, b) => a.id === b.id}
    />
  )
}
